import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { useAuth } from './AuthContext';
import { API_BASE, getNotifications } from '../lib/api';

/**
 * Shared source of truth for the notification badge count. Previously
 * lived inside the header component; extracted so BOTH the Home
 * "Notifications" tab chip and any other future consumer (mobile nav,
 * push toast, etc.) can subscribe to the exact same number without
 * double-polling the API.
 *
 * Behaviour preserved verbatim from Header.jsx:
 *   - Poll every 60 s so the count stays fresh without hammering the API
 *   - Refetch on window focus and on `pwm:notifications-changed` events
 *   - Optimistic `pwm:notifications-decrement` handling for instant
 *     badge decrement on dismissal
 *   - Live SSE via `/api/notifications/stream` for zero-latency updates
 *   - Falls back to poll-only if EventSource is unavailable
 *   - `count` reflects TOTAL notifications (read + unread), matching
 *     the product decision documented on the old header effect
 */
const NotificationsContext = createContext({
  count: 0,
  reload: () => {},
});

export const NotificationsProvider = ({ children }) => {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  const reload = useCallback(() => {
    if (!user) { setCount(0); return; }
    getNotifications({ limit: 40 })
      .then((data) => {
        // Prefer `total_count`. Fall back to `unread_count` on older
        // API responses so a rolling deploy doesn't hide the badge.
        const nextCount = Number(data.total_count ?? data.unread_count ?? 0) || 0;
        setCount(nextCount);
      })
      .catch(() => { /* non-fatal */ });
  }, [user]);

  useEffect(() => {
    if (!user) { setCount(0); return undefined; }
    let cancelled = false;
    const load = () => {
      getNotifications({ limit: 40 })
        .then((data) => {
          if (cancelled) return;
          const nextCount = Number(data.total_count ?? data.unread_count ?? 0) || 0;
          setCount(nextCount);
        })
        .catch(() => { /* non-fatal */ });
    };
    load();
    const id = setInterval(load, 60_000);
    const onFocus = () => load();
    const onDecrement = () => { setCount((n) => Math.max(0, n - 1)); };
    const onReconcile = () => { load(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener('pwm:notifications-decrement', onDecrement);
    window.addEventListener('pwm:notifications-changed', onReconcile);

    // Live SSE — see Header.jsx history for the full rationale. Browser
    // auto-reconnects on drops; we lean on that instead of hand-rolled
    // reconnect logic. Falls back silently to poll-only when
    // EventSource is unavailable (older browsers / SSR / test env).
    let es = null;
    try {
      const streamUrl = `${API_BASE}/notifications/stream`;
      es = new EventSource(streamUrl, { withCredentials: true });
      es.addEventListener('notif', () => { load(); });
      es.addEventListener('error', () => { /* non-fatal, auto-reconnect */ });
    } catch { /* EventSource unavailable — poll-only fallback */ }

    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pwm:notifications-decrement', onDecrement);
      window.removeEventListener('pwm:notifications-changed', onReconcile);
      if (es) { try { es.close(); } catch { /* noop */ } }
    };
  }, [user]);

  const value = useMemo(() => ({ count, reload }), [count, reload]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationsContext);
